/**
 * 日付を「MM/DD」形式にフォーマットする関数
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const month = date.getMonth() + 1; // 月は0から始まるので+1
    const day = date.getDate();

    return `${month}/${day}`;
  } catch (e) {
    return '';
  }
};

/**
 * 日付を「MM/DD」形式にフォーマットする関数（formatDateのエイリアス）
 */
export const formatDateMMDD = formatDate;

/**
 * 日付を「YYYYMMDD」形式に変換する関数
 */
export const formatDateYYYYMMDD = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // 月は0から始まる、2桁になるよう0埋め
    const day = date.getDate().toString().padStart(2, '0'); // 日も2桁になるよう0埋め

    return `${year}${month}${day}`;
  } catch (e) {
    return '';
  }
};

/**
 * 日付文字列（YYYYMMDD形式）を日付オブジェクトに変換する関数
 */
export const parseDateFromYYYYMMDD = (dateString: string | null): Date | null => {
  if (!dateString || dateString.length !== 8) return null;

  try {
    const year = parseInt(dateString.substring(0, 4));
    const month = parseInt(dateString.substring(4, 6));
    const day = parseInt(dateString.substring(6, 8));

    // 月と日の妥当性をチェック
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }

    // 月の範囲チェック（1-12）
    if (month < 1 || month > 12) {
      return null;
    }

    // 各月の最大日数
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // うるう年の判定
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    if (isLeapYear && month === 2) {
      daysInMonth[1] = 29;
    }

    // 日の範囲チェック
    if (day < 1 || day > daysInMonth[month - 1]) {
      return null;
    }

    // 有効な日付なのでDateオブジェクトを作成
    const date = new Date(year, month - 1, day);

    // 念のため、作成された日付が元の値と一致するか確認
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }

    return date;
  } catch (e) {
    return null;
  }
};

/**
 * 現在の日付と比較して指定された日付が過去かどうかを判定
 */
export const isDatePassed = (dateString: string | null): boolean => {
  if (!dateString) return false;

  const date = parseDateFromYYYYMMDD(dateString);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0); // 時間部分をリセット（日付のみで比較）

  return date < today;
};

/**
 * 日本の年度を取得する関数（4月1日から始まる）
 */
export const getJapaneseFiscalYear = (): { currentYear: number; previousYear: number } => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11 (0が1月)
  const currentYear = now.getFullYear() - (currentMonth < 3 ? 1 : 0);
  const previousYear = currentYear - 1;

  return { currentYear, previousYear };
};

/**
 * 年度の開始日を計算する関数
 */
export const getFiscalYearStartDate = (year: number): Date => {
  return new Date(year, 3, 1); // 4月1日（0-indexedなので月は3）
};

/**
 * 値と文言を組み合わせる関数（値がある場合のみ文言を表示）
 */
export const combineValueAndLabel = (value: string | null | undefined, label: string): string => {
  if (!value) return '';
  return `${value}${label}`;
};
